import styled from 'styled-components'
import { QueueExecutionsResponse } from '../../types/API'
import Address from '../general/Address'
import Network from '../general/Network'
import { capitalizeFirstLetter } from '../../utils/math-utils'
import moment from 'moment'

interface Props {
  data?: QueueExecutionsResponse[]
}

const DATE_FORMAT = 'MMM D, HH:mm:ss'

const statusConfig: Record<string, { color: string; bg: string; border: string }> = {
  enqueued:              { color: '#FFCC33', bg: 'rgba(255,204,51,0.08)',  border: 'rgba(255,204,51,0.2)'  },
  running:               { color: '#a89aff', bg: 'rgba(111,92,230,0.1)',   border: 'rgba(111,92,230,0.25)' },
  finished:              { color: '#33C2B0', bg: 'rgba(51,194,176,0.08)',  border: 'rgba(51,194,176,0.2)'  },
  'finished with errors':{ color: '#ff8927', bg: 'rgba(255,137,39,0.08)',  border: 'rgba(255,137,39,0.2)'  },
  stale:                 { color: '#555',    bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.08)'},
  denied:                { color: '#ff4444', bg: 'rgba(255,68,68,0.08)',   border: 'rgba(255,68,68,0.2)'   },
}

const fmt = (d: Date | undefined) => d ? moment(d).format(DATE_FORMAT) : '—'
const rel = (d: Date | undefined) => d ? moment(d).fromNow() : '—'

function QueueExecutionsTable({ data }: Props) {
  if (!data || data.length === 0) {
    return (
      <Empty>
        <EmptyIcon>○</EmptyIcon>
        <EmptyText>No executions found</EmptyText>
      </Empty>
    )
  }

  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>Namespace</Th>
            <Th>Mimic ID</Th>
            <Th>Network</Th>
            <Th>Status</Th>
            <Th>Allowed at</Th>
            <Th>Started at</Th>
            <Th>Finished at</Th>
            <Th>Pod Job ID</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => {
            const cfg = statusConfig[item.status] ?? statusConfig['stale']
            return (
              <tr key={`queue-execution-${index}`}>
                <Td><Namespace>{item.environment.namespace}</Namespace></Td>
                <Td>
                  <Address address={item.environment.mimicId} short showIdentity={false} withLink={false} />
                </Td>
                <Td><Network network={item.chainId} small /></Td>
                <Td>
                  <StatusBadge $color={cfg.color} $bg={cfg.bg} $border={cfg.border}>
                    {capitalizeFirstLetter(item.status)}
                  </StatusBadge>
                </Td>
                <Td>
                  <DateCell title={fmt(item.allowedAt)}>{rel(item.allowedAt)}</DateCell>
                </Td>
                <Td>
                  <DateCell title={fmt(item.startedAt)}>{item.startedAt ? rel(item.startedAt) : <Muted>—</Muted>}</DateCell>
                </Td>
                <Td>
                  <DateCell title={fmt(item.finishedAt)}>{item.finishedAt ? rel(item.finishedAt) : <Muted>—</Muted>}</DateCell>
                </Td>
                <Td><Mono>{item.podJobId ?? <Muted>—</Muted>}</Mono></Td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </TableWrap>
  )
}

export default QueueExecutionsTable

// ─── Styles ──────────────────────────────────────────────────────────────────

const TableWrap = styled.div`overflow-x: auto; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;`
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`
  padding: 10px 12px; text-align: left; color: #a1a1a1; font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`
const Td = styled.td`
  padding: 9px 12px; color: #a0a0a0; font-size: 13px; vertical-align: middle;
  border-bottom: 1px solid rgba(255,255,255,0.04); tr:last-child & { border-bottom: none; }
`
const Namespace = styled.span`font-size: 13px; color: #ededed; font-weight: 500;`
const Mono = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 11px; color: #777;`
const Muted = styled.span`color: #777;`

const StatusBadge = styled.span<{ $color: string; $bg: string; $border: string }>`
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 4px;
  font-size: 11px; font-weight: 500; white-space: nowrap;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $border }) => $border};
`

const DateCell = styled.span`
  font-family: 'SF Mono','Fira Code',monospace; font-size: 11px; color: #777;
  cursor: default;
  &[title]:hover { color: #a0a0a0; }
`

const Empty = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 48px 24px; gap: 8px;
  border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;
`
const EmptyIcon = styled.span`font-size: 20px; color: #2a2a2a;`
const EmptyText = styled.p`font-size: 13px; color: #777; margin: 0;`
